import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { Button } from 'react-bootstrap';
import NotificationTypeSection from './NotificationTypeSection';
import { getNotificationConfig, updateNotificationConfig } from '../UsersActions';
import { useIsMounted } from '../../helpers/isMounted';

const SEARCH_ALERT_CONFIG = 'SEARCH_ALERT';

const mapToNotificationConfig = (configs) =>
  configs.map(({ event, checked }) => ({ event, checked }));

function NotificationSettingForm({ userId, onCloseAlert }) {
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const [isLoading, setIsLoading] = useState(true);
  const [updated, setUpdated] = useState(false);

  const [configs, setConfigs] = useState({
    system: { EMAIL: [], PUSH: [] },
    search: { EMAIL: [], PUSH: [] },
  });

  const loadNotificationConfig = useCallback(async () => {
    if (!userId) return;
    setIsLoading(true);
    const data = await dispatch(getNotificationConfig(userId));

    if (!isMounted.current) return;

    const prepareSettings = (source, isSearch) =>
      source
        .filter(({ event }) =>
          isSearch ? event === SEARCH_ALERT_CONFIG : event !== SEARCH_ALERT_CONFIG,
        )
        .map((item) => (isSearch ? { ...item, eventMessage: 'Enabled' } : { ...item }));

    setConfigs({
      system: {
        EMAIL: prepareSettings(data.emailNotifications, false),
        PUSH: prepareSettings(data.pushNotifications, false),
      },
      search: {
        EMAIL: prepareSettings(data.emailNotifications, true),
        PUSH: prepareSettings(data.pushNotifications, true),
      },
    });

    setIsLoading(false);
  }, [dispatch, userId, isMounted]);

  useEffect(() => {
    loadNotificationConfig();
  }, [loadNotificationConfig]);

  const toggleChecked = (section, type, event) => {
    setConfigs((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [type]: prev[section][type].map((item) =>
          item.event === event ? { ...item, checked: !item.checked } : item,
        ),
      },
    }));
    setUpdated(true);
  };

  const handleUpdate = async () => {
    setIsLoading(true);
    const { system, search } = configs;

    try {
      const email = [...search.EMAIL, ...system.EMAIL];
      const push = [...search.PUSH, ...system.PUSH];

      await dispatch(
        updateNotificationConfig(
          userId,
          mapToNotificationConfig(push),
          mapToNotificationConfig(email),
        ),
      );
      if (isMounted.current) loadNotificationConfig();
    } catch {
      if (isMounted.current) loadNotificationConfig();
    } finally {
      if (isMounted.current) {
        setIsLoading(false);
        setUpdated(false);
      }
    }
  };

  return (
    <div>
      <NotificationTypeSection
        title="SYSTEM NOTIFICATIONS"
        emailSettings={configs.system.EMAIL}
        pushSettings={configs.system.PUSH}
        onChangeConfig={(type, event) => toggleChecked('system', type, event)}
      />
      <NotificationTypeSection
        title="SEARCH ALERTS"
        emailSettings={configs.search.EMAIL}
        pushSettings={configs.search.PUSH}
        onChangeConfig={(type, event) => toggleChecked('search', type, event)}
      />
      <div className="notification__buttons-container">
        <Button
          type="submit"
          className="rounded-pill btn-success"
          onClick={handleUpdate}
          disabled={isLoading || !updated}
        >
          Save
        </Button>
        <Button className="rounded-pill btn-danger" onClick={onCloseAlert}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

NotificationSettingForm.propTypes = {
  userId: PropTypes.string,
  onCloseAlert: PropTypes.func.isRequired,
};

NotificationSettingForm.defaultProps = {
  userId: '',
};

export default NotificationSettingForm;
